/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FeedStepper, FlexColumn, Typography } from 'design-system';
import { CheckIcon, LockIcon } from 'design-system/components/Icons';

function mapStepsToFeedStepper(steps) {
  const activeIndex = steps.findIndex((step) => step.status === 'active');
  const safeActiveIndex = activeIndex === -1 ? 0 : activeIndex;

  const feedSteps = steps.map((step) => {
    let marker;
    if (step.status === 'locked') {
      marker = <LockIcon size={14} aria-hidden />;
    } else if (
      step.status === 'completed'
      || (step.status === 'active' && step.contentComplete)
    ) {
      marker = <CheckIcon size={16} strokeWidth={2} aria-hidden />;
    }

    return {
      key: step.id,
      label: step.title,
      description: step.subtitle || undefined,
      marker,
    };
  });

  const activeStepComplete = steps.some(
    (step) => step.status === 'active' && step.contentComplete,
  );

  return { feedSteps, activeIndex: safeActiveIndex, activeStepComplete };
}

export default function ProgressSidebar({ steps }) {
  const intl = useIntl();
  const { feedSteps, activeIndex, activeStepComplete } = steps?.length
    ? mapStepsToFeedStepper(steps)
    : { feedSteps: [], activeIndex: 0, activeStepComplete: false };

  if (!feedSteps.length) {
    return null;
  }

  return (
    <FlexColumn align="stretch" fullWidth={false} gap="var(--ds-space-4)" className="ld-stepper">
      <Typography
        component="p"
        variant="bodyExtraSmall"
        fontWeight="semibold"
        className="ld-stepper__heading"
      >
        {intl.formatMessage({ id: 'ld-your-progress' })}
      </Typography>
      <FeedStepper
        steps={feedSteps}
        activeIndex={activeIndex}
        activeStepComplete={activeStepComplete}
        orientation="vertical"
        nodeVariant="outline"
        tone="primary"
        accent="var(--ds-theme-action-primary)"
        className={
          activeStepComplete
            ? 'ld-stepper__feed ld-stepper__feed--active-complete'
            : 'ld-stepper__feed'
        }
      />
    </FlexColumn>
  );
}

ProgressSidebar.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      stepNumber: PropTypes.number.isRequired,
      status: PropTypes.oneOf(['completed', 'active', 'pending', 'locked']).isRequired,
      contentComplete: PropTypes.bool,
    }),
  ),
};
